'use client';

/**
 * ObeyaPlanLensVendorsCard — the vendors as a portfolio, drawn in two lanes.
 * A vendor with no selected spend has no share to plot, so it sits in its own
 * narrow lane; vendors with spend spread across the main area by share. Height
 * is how many open items a vendor has options on, with the median as a dashed
 * line. Filter buttons show one lane or both.
 *
 * A renderer: lane, positions, tick marks and which points carry a label all
 * arrive from the BFF. The filter is the only local state.
 *
 * @see klyde_edge/klyde_intelligence/projects/plan_lenses.py
 *
 * @component_role: Card
 * @component_type: Card Renderer
 * @domain: Canvas / Obeya
 * @layer: presentation
 */

import { useMemo, useState } from 'react';
import { text } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import { useCardExport } from '@/lib/design/card-export';
import { useCardIdentity } from '@/lib/design/card-chrome';
import { PlanLensFrame } from './plan-lens-frame';
import { usePlanLenses } from './use-plan-lenses';

const WITH_SPEND = '#7fa2ff';
const NO_SPEND = '#e0a94f';
const PLOT_TEXT = 'text-[11px] text-muted-foreground';

// Which way a point's label runs, from the BFF's `paint.label_align`.
const LABEL_ALIGN: Record<string, string> = {
  start: 'left-3',
  center: '-translate-x-1/2 left-0',
  end: 'right-3',
};

type Show = 'all' | 'with_spend' | 'no_spend';

export function ObeyaPlanLensVendorsCard() {
  const { contentKey } = useCardIdentity();
  const { mcpId, strategy, d, isLoading, queryKey } = usePlanLenses();
  const [show, setShow] = useState<Show>('all');

  useCardExport(useMemo(() => ({
    content_key: contentKey ?? 'obeya_plan_lens_vendors',
    status: (d ? 'loaded' : mcpId && strategy ? 'loading' : 'idle') as 'loaded' | 'loading' | 'idle',
    query_key: queryKey,
    text: d ? d.labels.vendors_title : 'Vendors',
    json: d ? d.vendors : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [contentKey, d, mcpId, strategy]));

  if (!mcpId) return null;
  if (isLoading && !d) return <div className={cn(text.muted, 'p-6 text-center')}>…</div>;
  if (!d) return null;

  const L = d.labels;
  const v = d.vendors.data;
  // "1 item", "12 items" — the plural is picked here, the words come from the labels lane.
  const noun = (n: number) => (n === 1 ? L.item_word : L.items_word);
  const all = v.cells.flatMap((c) => c.vendors);
  const withSpend = all.filter((x) => x.paint.lane === 'with_spend');
  const noSpend = all.filter((x) => x.paint.lane === 'no_spend');
  const showMain = show !== 'no_spend' && withSpend.length > 0;
  const showLane = show !== 'with_spend' && noSpend.length > 0;
  const FILTERS: [Show, string][] = [['all', L.filter_all], ['with_spend', L.lane_with_spend], ['no_spend', L.lane_no_spend]];

  const median = v.paint.reach_boundary_y_pct != null && (
    <div className="absolute inset-x-0 border-t border-dashed border-foreground/40" style={{ bottom: `${v.paint.reach_boundary_y_pct}%` }} />
  );
  const point = (x: (typeof all)[number], left: number, color: string) => (
    <div key={x.vendor} className="absolute" style={{ left: `${left}%`, bottom: `${x.paint.y_pct}%` }}>
      <span className="absolute w-3 h-3 rounded-full -translate-x-1/2 translate-y-1/2 border-2 border-card" style={{ background: color }} />
      {x.paint.show_label && (
        <span className={cn(PLOT_TEXT, 'absolute bottom-0 translate-y-1/2 whitespace-nowrap', LABEL_ALIGN[x.paint.label_align])}>
          <b className="font-semibold text-foreground">{x.vendor}</b> {x.share_pct}% · {x.candidate_on_open_requirements} {noun(x.candidate_on_open_requirements)}
        </span>
      )}
    </div>
  );

  return (
    <PlanLensFrame title={L.vendors_title} meta={L.vendors_meta}>
      <div className="flex gap-2 flex-wrap mb-3">
        {FILTERS.map(([k, label]) => (
          <button
            key={k}
            type="button"
            onClick={() => setShow(k)}
            className={cn(text.muted, 'px-3 py-1 rounded-md border', show === k ? 'border-primary bg-primary/10 text-foreground font-semibold' : 'border-border')}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="relative h-[360px] rounded-lg bg-muted/30">
        <div className="absolute" style={{ left: 56, right: 18, top: 30, bottom: 44 }}>
          {v.paint.y_ticks.map((t) => (
            <span key={`y${t.value}`} className={cn(PLOT_TEXT, 'absolute right-full pr-2 translate-y-1/2 tabular-nums')} style={{ bottom: `${t.pct}%` }}>{t.value}</span>
          ))}
          <span className={cn(PLOT_TEXT, 'absolute -left-12 top-1/2 whitespace-nowrap origin-center -rotate-90 -translate-y-1/2')}>{L.axis_reach}</span>

          {showLane && (
            <div className="absolute inset-y-0 left-0 border-l border-b border-border bg-amber-500/5" style={{ width: showMain ? '24%' : '100%' }}>
              <span className={cn(PLOT_TEXT, 'absolute bottom-full mb-1.5 left-0 font-semibold uppercase tracking-wider')}>{L.lane_no_spend}</span>
              {median}
              {noSpend.map((x) => point(x, x.paint.lane_x_pct ?? 0, NO_SPEND))}
            </div>
          )}

          {showMain && (
            <div className="absolute inset-y-0 right-0 border-l border-b border-border" style={{ left: showLane ? 'calc(24% + 16px)' : 0 }}>
              <span className={cn(PLOT_TEXT, 'absolute bottom-full mb-1.5 left-0 font-semibold uppercase tracking-wider')}>{L.lane_with_spend}</span>
              {median}
              {v.reach_boundary_requirements != null && v.paint.reach_boundary_y_pct != null && (
                <span className={cn(PLOT_TEXT, 'absolute right-1.5 mb-0.5')} style={{ bottom: `${v.paint.reach_boundary_y_pct}%` }}>
                  {L.median_word} {v.reach_boundary_requirements} {noun(v.reach_boundary_requirements)}
                </span>
              )}
              <div className="absolute inset-y-0 inset-x-[3%]">
                {v.paint.x_ticks.map((t) => (
                  <span key={`x${t.value}`} className={cn(PLOT_TEXT, 'absolute top-full pt-1 -translate-x-1/2 tabular-nums')} style={{ left: `${t.pct}%` }}>{t.value}%</span>
                ))}
                {withSpend.map((x) => point(x, x.paint.x_pct ?? 0, WITH_SPEND))}
              </div>
              <span className={cn(PLOT_TEXT, 'absolute top-full mt-6 left-1/2 -translate-x-1/2 whitespace-nowrap')}>{L.axis_share}</span>
            </div>
          )}
        </div>
      </div>
    </PlanLensFrame>
  );
}
